<script>
    import {debounce} from 'lodash';

    export default {
        name: 'VueImage',

        props: {
            source: {
                type: String,
                required: true,
            },
            altText: {
                type: String,
                required: false,
                default: 'Image',
            }
        },

        data() {
            return {
                observer: null,
                resizeDebouncer: null,

                loading: true,
                requiresLoad: true,
                isVisible: false,
            };
        },

        created() {
            this.resizeDebouncer = debounce(this.onResize, 250);

            window.addEventListener('resize', this.resizeDebouncer);
        },

        mounted() {
            // If IntersectionObservers are possible, utilize them to delay loading
            // Otherwise, load the image
            this.supportsIntersection ?
                this.observe() :
                this.loadImage();
        },

        beforeUnmount() {
            // If IntersectionObservers are possible, destroy the current instance
            // Otherwise, null out
            this.supportsIntersection ?
                this.unobserve() :
                null;
        },

        unmounted() {
            window.removeEventListener('resize', this.onResize);
        },

        computed: {
            supportsIntersection() {
                return window['IntersectionObserver'];
            },
        },

        methods: {
            observe() {
                this.observer = new IntersectionObserver((entries, observer) => {
                    entries.forEach((entry) => {
                        // Mark if the current element is visible or not
                        this.isVisible = entry.isIntersecting;

                        // If the entry is visible and requires a load, fetch the image
                        if (this.isVisible && this.requiresLoad) {
                            this.loadImage();
                        }
                    });
                }, {
                    root: null,
                    threshold: 0,
                });

                this.observer.observe(this.$el);
            },
            unobserve() {
                if (this.observer) {
                    this.observer.disconnect();
                    this.observer = null;
                }
            },
            onResize() {
                // If the image doesn't already require a load and object fit is a variable one
                if (!this.requiresLoad && ['fill', 'contain', 'cover'].includes(this.objectFit())) {
                    // Mark the image as requiring a load
                    this.requiresLoad = true;

                    // If visible, load the image
                    if(this.isVisible) {
                        this.loadImage();
                    }
                }
            },
            loadImage() {
                this.loading = true;

                // Load the image
                this.$el.src = this.imageUrl();
                this.$el.alt = this.altText;

                // The image no longer requires a load
                this.requiresLoad = false;
            },
            imageUrl() {
                const urlBuilder = new URL(`${window.location.origin}/image`);

                urlBuilder.searchParams.append('s', String(this.source));
                urlBuilder.searchParams.append('h', String(this.$el.clientHeight));
                urlBuilder.searchParams.append('w', String(this.$el.clientWidth));
                urlBuilder.searchParams.append('fit', String(this.objectFit()));

                return urlBuilder.href;
            },
            objectFit() {
                return window.getComputedStyle(this.$el, null).objectFit;
            },
            finishedLoad() {
                this.loading = false;
            },
        },
    };
</script>

<template>
    <img
        :class="{'bg-gray-300 animate-pulse': loading}"
        @load="finishedLoad"
        alt=""
    >
</template>